import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { HiChevronDown, HiChevronRight, HiFilter, HiClipboardCopy } from 'react-icons/hi';

const formatDate = (dateString) => {
  try {
    return format(new Date(dateString), 'MMM d, yyyy · h:mm a');
  } catch {
    return '—';
  }
};

const formatDuration = (minutes) => {
  if (typeof minutes !== 'number' || Number.isNaN(minutes)) return '—';
  if (minutes < 1) return `${Math.round(minutes * 60)}s`;
  return `${minutes.toFixed(1)} min`;
};

const formatCredits = (minutes) => {
  if (typeof minutes !== 'number' || Number.isNaN(minutes)) return '—';
  return `${minutes.toFixed(2)}`;
};

function compareCalls(a, b, key) {
  if (key === 'recording_url') {
    const ar = !!a.recording_url;
    const br = !!b.recording_url;
    if (ar === br) return 0;
    return ar ? 1 : -1;
  }
  if (key === 'completed') {
    const ac = !!a.completed;
    const bc = !!b.completed;
    if (ac === bc) return 0;
    return ac ? 1 : -1;
  }
  let av = a[key];
  let bv = b[key];
  if (key === 'created_at') {
    av = av ? new Date(av).getTime() : 0;
    bv = bv ? new Date(bv).getTime() : 0;
  }
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === 'string' && typeof bv === 'string') {
    return av.localeCompare(bv, undefined, { sensitivity: 'base' });
  }
  if (av < bv) return -1;
  if (av > bv) return 1;
  return 0;
}

const MetaItem = ({ label, children }) => (
  <div className="min-w-0">
    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-500">
      {label}
    </div>
    <div className="mt-0.5 text-sm text-slate-800 dark:text-slate-200 break-words">
      {children ?? '—'}
    </div>
  </div>
);

const CallHistoryTable = ({ calls }) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc',
  });
  const [columnFilters, setColumnFilters] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpand = useCallback((callId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(callId)) next.delete(callId);
      else next.add(callId);
      return next;
    });
  }, []);

  const filteredCalls = useMemo(() => {
    let result = [...calls];

    Object.entries(columnFilters).forEach(([key, value]) => {
      if (!value) return;
      const filterValue = value.toLowerCase();
      result = result.filter((call) => {
        if (key === 'recording_url') {
          if (filterValue === 'yes' || filterValue === 'true') return !!call.recording_url;
          if (filterValue === 'no' || filterValue === 'false') return !call.recording_url;
          return true;
        }
        if (key === 'completed') {
          if (filterValue.includes('complete')) return !!call.completed;
          if (filterValue.includes('progress')) return !call.completed;
        }
        const cellValue = call[key];
        if (cellValue === null || cellValue === undefined) return false;
        return cellValue.toString().toLowerCase().includes(filterValue);
      });
    });

    result.sort((a, b) => {
      const cmp = compareCalls(a, b, sortConfig.key);
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [calls, columnFilters, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleColumnFilterChange = (key, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleFilter = (key) => {
    setActiveFilter((prev) => (prev === key ? null : key));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-call-history-filter]')) {
        setActiveFilter(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderFilterPopover = (colKey, label, type = 'text') => {
    if (activeFilter !== colKey) return null;
    return (
      <div
        className="absolute right-0 mt-1 z-30 w-56 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 shadow-xl p-2"
        data-call-history-filter
      >
        {type === 'select' ? (
          <select
            value={columnFilters[colKey] || ''}
            onChange={(e) => handleColumnFilterChange(colKey, e.target.value)}
            className="w-full app-field text-sm py-2"
            autoFocus
          >
            <option value="">All</option>
            <option value="yes">Has recording</option>
            <option value="no">No recording</option>
          </select>
        ) : (
          <input
            type="text"
            placeholder={`Filter ${label}…`}
            value={columnFilters[colKey] || ''}
            onChange={(e) => handleColumnFilterChange(colKey, e.target.value)}
            className="w-full app-field text-sm py-2"
            autoFocus
          />
        )}
      </div>
    );
  };

  const SortFilterHead = ({ colKey, label, minW, filterType = 'text' }) => {
    const sorted = sortConfig.key === colKey;
    const filtered = !!columnFilters[colKey];
    return (
      <th
        className="sticky top-0 z-10 px-3 py-3 text-left align-middle bg-[var(--app-table-head)] border-b border-[var(--app-border)] backdrop-blur-md bg-opacity-95"
        style={{ minWidth: minW }}
      >
        <div className="flex items-center justify-between gap-1">
          <button
            type="button"
            onClick={() => requestSort(colKey)}
            className="flex items-center gap-1 min-w-0 text-left rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent)] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
          >
            <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 truncate">
              {label}
            </span>
            {sorted && (
              <span className="shrink-0 text-[var(--app-accent)] text-xs" aria-hidden>
                {sortConfig.direction === 'asc' ? '▲' : '▼'}
              </span>
            )}
          </button>
          <div className="relative shrink-0" data-call-history-filter>
            <button
              type="button"
              onClick={() => toggleFilter(colKey)}
              className={`p-1.5 rounded-md transition-colors ${
                filtered
                  ? 'text-[var(--app-accent)] bg-[var(--app-accent-soft)]'
                  : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80'
              }`}
              title={`Filter ${label}`}
              aria-label={`Filter column ${label}`}
            >
              <HiFilter className="w-3.5 h-3.5" />
            </button>
            {renderFilterPopover(colKey, label, filterType)}
          </div>
        </div>
      </th>
    );
  };

  const COL_COUNT = 9;

  const DetailGrid = ({ call }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 sm:p-5 bg-slate-50/80 dark:bg-slate-950/40 border-t border-[var(--app-border)]">
      <MetaItem label="Notes">{call.notes}</MetaItem>
      <MetaItem label="Dialog flow">
        {call.dialogFlow_name || call.dialogFlow_id ? (
          <span>
            {call.dialogFlow_name && <span className="font-medium">{call.dialogFlow_name}</span>}
            {call.dialogFlow_name && call.dialogFlow_id && (
              <span className="text-slate-500 dark:text-slate-400"> · </span>
            )}
            {call.dialogFlow_id && (
              <code className="text-xs text-slate-600 dark:text-slate-300">{call.dialogFlow_id}</code>
            )}
          </span>
        ) : null}
      </MetaItem>
      <MetaItem label="Answered by">{call.answered_by}</MetaItem>
      <MetaItem label="Ended by">{call.call_ended_by}</MetaItem>
      <MetaItem label="Transferred to">{call.transferred_to}</MetaItem>
      <MetaItem label="Batch ID">{call.batch_id}</MetaItem>
      {call.error_message ? (
        <div className="sm:col-span-2 lg:col-span-3 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400">
            Error
          </div>
          <div className="mt-0.5 text-sm text-red-700 dark:text-red-300 break-words">{call.error_message}</div>
        </div>
      ) : null}
      {call.summary ? (
        <div className="sm:col-span-2 lg:col-span-3 min-w-0">
          <MetaItem label="Summary">{call.summary}</MetaItem>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="call-history-table-root">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1 pb-3">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          <span className="font-semibold text-slate-900 dark:text-slate-100">{filteredCalls.length}</span>
          {' '}
          {filteredCalls.length === 1 ? 'call' : 'calls'}
          {filteredCalls.length !== calls.length && (
            <span className="text-slate-500 dark:text-slate-500"> · filtered from {calls.length}</span>
          )}
        </p>
      </div>

      {/* Mobile: cards */}
      <div className="md:hidden space-y-3">
        {filteredCalls.map((call) => {
          const open = expanded.has(call.call_id);
          return (
            <div
              key={call.call_id}
              className="rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] shadow-sm overflow-hidden"
            >
              <div className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs text-slate-500 dark:text-slate-400">{formatDate(call.created_at)}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <Link
                        to={`/app/calls/${call.call_id}`}
                        className="text-sm font-semibold text-[var(--app-accent)] hover:underline truncate"
                      >
                        {call.call_id?.slice(0, 8)}…
                      </Link>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(call.call_id)}
                        className="p-1 rounded-md text-slate-400 hover:text-[var(--app-accent)] hover:bg-[var(--app-accent-soft)]"
                        title="Copy call ID"
                      >
                        <HiClipboardCopy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <span
                    className={`shrink-0 px-2.5 py-1 text-xs font-semibold rounded-full border ${
                      call.completed
                        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/25'
                        : 'bg-amber-500/10 text-amber-800 dark:text-amber-200 border-amber-500/25'
                    }`}
                  >
                    {call.completed ? 'Done' : 'Active'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] font-bold uppercase text-slate-500">Direction</div>
                    <div className="text-slate-800 dark:text-slate-200">{call.direction || 'outbound'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase text-slate-500">Duration</div>
                    <div className="text-slate-800 dark:text-slate-200">
                      {formatDuration(call.call_length)}
                      <span className="text-slate-500 dark:text-slate-400 text-xs"> · {formatCredits(call.call_length)} cr</span>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] font-bold uppercase text-slate-500">To / From</div>
                    <div className="text-slate-800 dark:text-slate-200 truncate">{call.to || '—'}</div>
                    <div className="text-slate-600 dark:text-slate-400 truncate text-xs">{call.from || '—'}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {call.recording_url ? (
                    <Link
                      to={`/app/calls/${call.call_id}?tab=recording`}
                      className="text-sm font-medium text-[var(--app-accent)]"
                    >
                      Recording
                    </Link>
                  ) : (
                    <span className="text-sm text-slate-400">No recording</span>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleExpand(call.call_id)}
                    className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-slate-600 dark:text-slate-300"
                  >
                    {open ? 'Less' : 'More'}
                    {open ? <HiChevronDown className="w-4 h-4" /> : <HiChevronRight className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {open && <DetailGrid call={call} />}
            </div>
          );
        })}
      </div>

      {/* Desktop */}
      <div className="hidden md:block rounded-xl border border-[var(--app-border)] bg-[var(--app-table-body)] shadow-[0_1px_0_rgba(15,23,42,0.04)] dark:shadow-none overflow-x-auto">
        <table className="w-full border-collapse text-sm" style={{ minWidth: '56rem' }}>
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-30 w-10 px-2 py-3 bg-[var(--app-table-head)] border-b border-[var(--app-border)] shadow-[4px_0_12px_-4px_rgba(0,0,0,0.08)] dark:shadow-[4px_0_12px_-4px_rgba(0,0,0,0.4)]" />
              <SortFilterHead colKey="created_at" label="When" minW="10.5rem" />
              <SortFilterHead colKey="call_id" label="Call ID" minW="9rem" />
              <SortFilterHead colKey="direction" label="Direction" minW="5.5rem" />
              <SortFilterHead colKey="to" label="To" minW="7.5rem" />
              <SortFilterHead colKey="from" label="From" minW="7.5rem" />
              <SortFilterHead colKey="completed" label="Status" minW="6.5rem" />
              <SortFilterHead colKey="call_length" label="Duration" minW="7rem" />
              <th
                className="sticky top-0 z-10 px-3 py-3 text-left align-middle bg-[var(--app-table-head)] border-b border-[var(--app-border)]"
                style={{ minWidth: '5.5rem' }}
              >
                <div className="flex items-center justify-between gap-1">
                  <button
                    type="button"
                    onClick={() => requestSort('recording_url')}
                    className="flex items-center gap-1 min-w-0 text-left rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent)]"
                  >
                    <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                      Rec
                    </span>
                    {sortConfig.key === 'recording_url' && (
                      <span className="text-[var(--app-accent)] text-xs">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </button>
                  <div className="relative shrink-0" data-call-history-filter>
                    <button
                      type="button"
                      onClick={() => toggleFilter('recording_url')}
                      className={`p-1.5 rounded-md transition-colors ${
                        columnFilters.recording_url
                          ? 'text-[var(--app-accent)] bg-[var(--app-accent-soft)]'
                          : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80'
                      }`}
                      aria-label="Filter recording"
                    >
                      <HiFilter className="w-3.5 h-3.5" />
                    </button>
                    {renderFilterPopover('recording_url', 'recording', 'select')}
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredCalls.map((call) => {
              const open = expanded.has(call.call_id);
              return (
                <React.Fragment key={call.call_id}>
                  <tr className="group border-b border-[var(--app-border)] hover:bg-[var(--app-table-row-hover)] transition-colors">
                    <td className="sticky left-0 z-[2] w-10 px-2 py-3 bg-[var(--app-table-body)] group-hover:bg-[var(--app-table-row-hover)] border-b border-[var(--app-border)] shadow-[4px_0_12px_-4px_rgba(0,0,0,0.06)] dark:shadow-[4px_0_12px_-4px_rgba(0,0,0,0.35)]">
                      <button
                        type="button"
                        onClick={() => toggleExpand(call.call_id)}
                        className="p-1 rounded-md text-slate-500 hover:bg-slate-200/80 dark:hover:bg-slate-700/80"
                        aria-expanded={open}
                        aria-label={open ? 'Collapse row' : 'Expand row'}
                      >
                        {open ? <HiChevronDown className="w-4 h-4" /> : <HiChevronRight className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300 tabular-nums border-b border-[var(--app-border)]">
                      {formatDate(call.created_at)}
                    </td>
                    <td className="px-3 py-3 border-b border-[var(--app-border)]">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Link
                          to={`/app/calls/${call.call_id}`}
                          className="font-mono text-xs font-semibold text-[var(--app-accent)] hover:underline truncate"
                        >
                          {call.call_id?.slice(0, 10)}…
                        </Link>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(call.call_id)}
                          className="shrink-0 p-1 rounded-md text-slate-400 hover:text-[var(--app-accent)]"
                          title="Copy ID"
                        >
                          <HiClipboardCopy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 capitalize text-slate-700 dark:text-slate-200 border-b border-[var(--app-border)]">
                      {call.direction || 'outbound'}
                    </td>
                    <td className="px-3 py-3 max-w-[10rem] border-b border-[var(--app-border)]">
                      <div className="font-mono text-xs text-slate-700 dark:text-slate-200 truncate" title={call.to || ''}>
                        {call.to || '—'}
                      </div>
                    </td>
                    <td className="px-3 py-3 max-w-[10rem] border-b border-[var(--app-border)]">
                      <div className="font-mono text-xs text-slate-700 dark:text-slate-200 truncate" title={call.from || ''}>
                        {call.from || '—'}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap border-b border-[var(--app-border)]">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full border ${
                          call.completed
                            ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20'
                            : 'bg-amber-500/10 text-amber-800 dark:text-amber-200 border-amber-500/20'
                        }`}
                      >
                        {call.completed ? 'Completed' : 'In progress'}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-700 dark:text-slate-200 border-b border-[var(--app-border)]">
                      <div className="tabular-nums">{formatDuration(call.call_length)}</div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 tabular-nums">
                        {formatCredits(call.call_length)} credits
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap border-b border-[var(--app-border)]">
                      {call.recording_url ? (
                        <Link
                          to={`/app/calls/${call.call_id}?tab=recording`}
                          className="text-xs font-semibold text-[var(--app-accent)] hover:underline"
                        >
                          Open
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-slate-50/50 dark:bg-slate-950/20">
                      <td colSpan={COL_COUNT} className="p-0 border-b border-[var(--app-border)]">
                        <DetailGrid call={call} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CallHistoryTable;
